/**
 * v0.9.0 §1.1 — plane switcher state for a board.
 *
 * Derives the planes in play from the model; the SELECTION delegates to
 * ActiveViewContext's `activePlane` — the one plane store every projecting
 * surface reads (useViewSurface, the BoardSurface HUD chip). A local useState
 * here split the state in two: the ⌘F plane pick and the edited-away reset
 * acted on an orphaned copy nothing rendered (r48). The selection resets when
 * the model no longer has the chosen plane, and exposes the member ids to
 * FOCUS on the board (empty for primary/no plane).
 */

import { useEffect, useMemo } from "react";
import type { ArchitectureModel } from "@archlang/scene/arch-model";
import { computePlaneInfo, planeMemberIds } from "../planeFilter.js";
import { useActiveView } from "../workbench/ActiveViewContext.js";

export interface PlaneSwitcher {
  /** Non-primary planes in the model, sorted. Empty ⇒ hide the switcher. */
  readonly planes: readonly string[];
  /** Selected plane, or null for the primary (whole-model) view. */
  readonly selected: string | null;
  readonly select: (plane: string | null) => void;
  /** Board node ids to focus for the selection ([] when primary). */
  readonly memberIds: readonly string[];
}

export function usePlaneSwitcher(model: ArchitectureModel | null): PlaneSwitcher {
  const info = useMemo(() => computePlaneInfo(model?.modules ?? []), [model]);
  const { activePlane, setActivePlane } = useActiveView();
  const stale = activePlane !== null && !info.planes.includes(activePlane);
  // A plane edited out of existence falls back to primary. Reset the STORED
  // selection (in an effect — the state lives in the ActiveViewProvider
  // parent, so an adjust-during-render would set a parent's state mid-render),
  // not just mask it — else the stale pick resurrects when the same plane
  // later returns (undo/redo, toggling an aspect off then on) and silently
  // re-dims a board the user never re-focused. The MASK below covers the
  // pre-effect frame so the projection never renders the deleted plane.
  useEffect(() => {
    if (stale) setActivePlane(null);
  }, [stale, setActivePlane]);
  const selected = stale ? null : activePlane;
  const memberIds = useMemo(() => planeMemberIds(info, selected), [info, selected]);
  return {
    planes: info.planes,
    selected,
    select: setActivePlane,
    memberIds,
  };
}
